import { LANGUAGE_LIST, LANGUAGE_NAME_LIST, type Lang } from './list'

/**
 * Translation provider registry — the single source of truth for, per language:
 *   - which providers CAN translate it (capability), and
 *   - what code each provider uses for it (so callers don't hardcode mappings),
 *   - which URL slug (subdomain label / subfolder prefix) serves it.
 *
 * This is the "one big table" (matrix: language × provider). A provider entry
 * present = that provider supports the language and here is its code; absent =
 * the provider cannot do this language.
 *
 * IMPORTANT — capability ≠ selection. This table only answers "who CAN".
 * "Who do we actually pick" lives in the resolver (default Yandex → DeepSeek →
 * …, fallback chain), NOT here. See ROADMAP.md → "Реестр провайдеров перевода".
 *
 * Names (`name`, `nativeName`) still originate from the TSV-generated
 * LANGUAGE_NAME_LIST and are merged in below, so there is no duplicate source.
 */

export type ProviderId = 'yandex' | 'deepseek' | 'deepl'

/** Provider → the code that provider uses for a language. Absent = unsupported. */
export interface ProviderCodes {
	yandex?: string
	deepseek?: string
	deepl?: string
}

export interface LangRegistryEntry {
	code: Lang
	name: string
	nativeName: string
	/**
	 * URL label for this language, used in BOTH routing modes:
	 *   - subdomain mode: `slug.site.com` (e.g. `ru` → ru.site.com)
	 *   - subfolder mode: `/slug/path`   (e.g. `ru` → /ru/about)
	 * Defaults to the language code; immutable once live (breaks URLs + SEO).
	 */
	slug: string
	providers: ProviderCodes
}

/**
 * Languages Yandex Cloud Translate does NOT support. Everything else in our
 * list it does, using the same code we use. Verified against Yandex's official
 * supported-languages list (May 2026): of our 90 languages it covers 88 — only
 * Traditional Chinese (`zh-Hant`, Yandex has simplified `zh` only) and Filipino
 * (`fil`, Yandex has Tagalog `tl` but no `fil`) are missing.
 */
const YANDEX_UNSUPPORTED = new Set<Lang>(['zh-Hant', 'fil'])

/**
 * Rare per-language slug overrides. Default is the language code itself.
 * HARD RULE: once a language is live in production its slug must not change — it
 * would break existing URLs (subdomain AND subfolder) and SEO. Empty for now.
 */
const SLUG_OVERRIDES: Partial<Record<Lang, string>> = {}

/**
 * DeepL target-language codes, keyed by our language code. DeepL is currently
 * DISABLED (expensive + weak quality in our chunking), so these are laid in for
 * the future only — taken from DeepL's next-gen supported list (May 2026).
 * RE-VERIFY against DeepL docs before actually enabling the provider.
 */
const DEEPL_CODES: Partial<Record<Lang, string>> = {
	af: 'AF',
	ar: 'AR',
	az: 'AZ',
	ba: 'BA',
	be: 'BE',
	bg: 'BG',
	bn: 'BN',
	bs: 'BS',
	ca: 'CA',
	cs: 'CS',
	cy: 'CY',
	da: 'DA',
	de: 'DE',
	el: 'EL',
	en: 'EN-US',
	eo: 'EO',
	es: 'ES',
	et: 'ET',
	eu: 'EU',
	fa: 'FA',
	fi: 'FI',
	fr: 'FR',
	ga: 'GA',
	gl: 'GL',
	gu: 'GU',
	he: 'HE',
	hi: 'HI',
	hr: 'HR',
	ht: 'HT',
	hu: 'HU',
	hy: 'HY',
	id: 'ID',
	is: 'IS',
	it: 'IT',
	ja: 'JA',
	jv: 'JV',
	ka: 'KA',
	kk: 'KK',
	ko: 'KO',
	ky: 'KY',
	la: 'LA',
	lb: 'LB',
	lt: 'LT',
	lv: 'LV',
	mg: 'MG',
	mi: 'MI',
	mk: 'MK',
	ml: 'ML',
	mn: 'MN',
	mr: 'MR',
	ms: 'MS',
	mt: 'MT',
	my: 'MY',
	ne: 'NE',
	nl: 'NL',
	no: 'NB',
	pa: 'PA',
	pl: 'PL',
	pt: 'PT-PT',
	ro: 'RO',
	ru: 'RU',
	sk: 'SK',
	sl: 'SL',
	sq: 'SQ',
	sr: 'SR',
	su: 'SU',
	sv: 'SV',
	sw: 'SW',
	ta: 'TA',
	te: 'TE',
	tg: 'TG',
	th: 'TH',
	tl: 'TL',
	tr: 'TR',
	tt: 'TT',
	uk: 'UK',
	ur: 'UR',
	uz: 'UZ',
	vi: 'VI',
	xh: 'XH',
	yi: 'YI',
	zh: 'ZH-HANS',
	'zh-Hant': 'ZH-HANT',
	// Not supported by DeepL: am, gd, km, kn, lo, si, fil.
}

function buildEntry(code: Lang): LangRegistryEntry {
	const meta = LANGUAGE_NAME_LIST[code]
	const providers: ProviderCodes = {
		// DeepSeek is an LLM prompted with the full English language name, so it
		// handles every language in our list. Its "code" is that English name.
		deepseek: meta.name,
	}
	if (!YANDEX_UNSUPPORTED.has(code)) {
		providers.yandex = code
	}
	if (DEEPL_CODES[code]) {
		providers.deepl = DEEPL_CODES[code]
	}

	return {
		code,
		name: meta.name,
		nativeName: meta.nativeName,
		// Slug is a URL/host label, always lowercase — the code may carry a
		// Title-case script subtag (e.g. `zh-Hant` → slug `zh-hant`).
		slug: SLUG_OVERRIDES[code] ?? code.toLowerCase(),
		providers,
	}
}

export const LANGUAGE_REGISTRY: Record<Lang, LangRegistryEntry> = Object.fromEntries(
	LANGUAGE_LIST.map((code) => [code, buildEntry(code)])
) as Record<Lang, LangRegistryEntry>

/** Providers capable of translating into `code`, in no particular order. */
export function providersFor(code: Lang): ProviderId[] {
	const p = LANGUAGE_REGISTRY[code].providers
	return (['yandex', 'deepseek', 'deepl'] as const).filter((id) => p[id] !== undefined)
}

/** English display name for `code` (e.g. zh-Hant → "Chinese (Traditional)"). Undefined if unknown. */
export function nameFor(code: Lang): string | undefined {
	return LANGUAGE_REGISTRY[code]?.name
}

/** The URL slug for `code` — subdomain label / subfolder prefix (defaults to the code). */
export function slugFor(code: Lang): string {
	return LANGUAGE_REGISTRY[code].slug
}

/**
 * Reverse of slugFor: map a URL slug back to its language code, or undefined if
 * no language uses that slug. Used to parse `/<slug>/` path prefixes and
 * `<slug>.host` subdomains back to a language.
 */
const SLUG_TO_LANG: Record<string, Lang> = Object.fromEntries(
	LANGUAGE_LIST.map((code) => [LANGUAGE_REGISTRY[code].slug, code])
)
export function langForSlug(slug: string): Lang | undefined {
	// Match case-insensitively: a `/zh-Hant/` or `/ZH-HANT/` URL resolves the
	// same as `/zh-hant/`. Slugs are stored lowercase.
	return SLUG_TO_LANG[slug.toLowerCase()]
}
